import {
  Badge,
  Box,
  Button,
  Container,
  H5,
  Icon,
  Inline,
  Stack,
} from '@amboss/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { createSearchParams, useNavigate, useSearchParams } from 'react-router-dom';
import { config } from '../../config';
import useBottomModal from '../../hooks/useBottomModal';
import useEditProfile from '../../hooks/useEditProfile';
import { useModal } from '../../hooks/useModal';
import useProfile from '../../hooks/useProfile';
import { CreditType } from '../../interfaces/credit';
import { creditsSelector } from '../../store/credits/creditsSlice';
import { isCourseCreditType } from '../../utils';
import {
  getCourseIdentifierFromItems,
  getCourseSurveyLink,
} from '../../utils/courseSurvey';
import Tooltip from '../atoms/Tooltip';
import ClaimModal from './ClaimModal';
import CourseClaimModal from './CourseClaimModal';

const BottomModal = () => {
  const isDesktopOrTablet = useMediaQuery({
    query: '(min-width: 521px)',
  });
  const { t } = useTranslation();
  const { profile } = useProfile();
  const navigate = useNavigate();
  const { openProfile } = useEditProfile();
  const [searchParams] = useSearchParams();
  const { selectedCount, creditIds, resetBottomModalForm } = useBottomModal();
  const { openModal, closeModal } = useModal();
  const creditsType = useSelector(creditsSelector);

  // lifecycle cleanup: form and modal should reset on component init and destroy
  useEffect(() => {
    resetBottomModalForm();
    closeModal();

    return () => {
      resetBottomModalForm();
      closeModal();
    };
  }, []);

  const openProfileHandler = () => {
    openProfile();
    navigate({ pathname: '/', search: `?${createSearchParams(searchParams)}` });
  };

  const handleClaimCredits = () => {
    if (isCourseCreditType(creditsType.type)) {
      const [selectedCourseId] = creditIds;
      if (!selectedCourseId) {
        return;
      }

      const courseIdentifier = getCourseIdentifierFromItems(
        creditsType.items,
        selectedCourseId,
      );
      const surveyUrl = getCourseSurveyLink(creditsType.type, courseIdentifier);

      openModal({
        title: '',
        modalWidth: '94vw',
        modalMaxWidth: 1200,
        modalHeight: '92vh',
        modalMaxHeight: '92vh',
        onClose: () => closeModal(),
        children: (
          <CourseClaimModal creditIds={[selectedCourseId]} surveyUrl={surveyUrl} />
        ),
      });
      return;
    }

    openModal({
      title: t('credits.tabs.tab2.claim.modal.title'),
      onClose: () => closeModal(),
      children: <ClaimModal creditIds={creditIds} />,
    });
  };

  const getButtonText = (type: string) => {
    if (selectedCount < 0) {
      return t(`credits.tabs.tab2.bottomModal.${type}.disabled`);
    } else {
      return t(`credits.tabs.tab2.bottomModal.${type}.enabled`, { count: selectedCount });
    }
  };

  return (
    <Box>
      <Container elevation={4}>
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: '50%',
            width: isDesktopOrTablet ? 'calc(100% - 100px)' : window.innerWidth,
            maxWidth: '944px',
            transform: 'translate(-50%, 0)',
            minHeight: 94,
            zIndex: 999,
            backgroundColor: 'white',
            borderTopLeftRadius: 15,
            borderTopRightRadius: 15,
            boxShadow: '0px 4px 20px rgba(0,0,0,0.2)',
          }}
        >
          <Box space={'xl'}>
            <Stack>
              <Inline vAlignItems={'center'}>
                {creditsType.type === 'abfm' ? (
                  <>
                    <H5>{t('credits.tabs.tab2.bottomModal.titleClaim')}</H5>
                  </>
                ) : (
                  <>
                    <H5>{t('credits.tabs.tab2.bottomModal.titleSubmit')}</H5>
                    <span data-tip data-for='global'>
                      <Icon name={'help-circle'} size={'s'} />
                    </span>
                    <Tooltip text={t('credits.tabs.tab2.bottomModal.tip')} />
                    {profile?.boards && profile.boards?.length > 0 && (
                      <Inline>
                        {profile.boards.map((board: any, i: number) => {
                          return <Badge key={i} text={board.label} color={'gray'} />;
                        })}
                      </Inline>
                    )}
                  </>
                )}
              </Inline>
              <Inline vAlignItems={'center'} alignItems={'spaceBetween'}>
                <Button
                  variant='tertiary'
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={openProfileHandler}
                >
                  {creditsType.type !== 'abfm' && t('credits.tabs.tab2.bottomModal.link')}
                </Button>
                <Button disabled={!(selectedCount > 0)} onClick={handleClaimCredits}>
                  {creditsType.type === 'abfm'
                    ? getButtonText('buttonClaim')
                    : getButtonText('buttonSubmit')}
                </Button>
              </Inline>
            </Stack>
          </Box>
        </div>
      </Container>
    </Box>
  );
};

export default BottomModal;
